import {
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";
import { useAppSelector } from "../../Reducers/store";
import PaginatedTable from "../Common/PaginatedTable";
import { Link } from "react-router-dom";
import { HiEye } from "react-icons/hi";
import { Equipment } from "../../Models/Equipment";
import { Tooltip, Button } from "flowbite-react";
import { formatNumbers } from "../../Utils";
import {
  EquipmentTypeEnums,
  EquipmentTypeStringEnums,
} from "../../Models/EquipmentTypeEnums";
import OrderApi from "../../Api/OrderApi";
import { OrderItem } from "../../Models/OrderItem";
import { Order } from "../../Models/Order";
import { toast } from "react-toastify";
import EquipmentApi from "../../Api/EquipmentApi";
import EmailModal from "../Forms/WarehouseAvailability";

export default function EquipmentListTable({
  data,
  manufacturerId,
  equipmentType,
}: {
  data: Equipment[];
  manufacturerId: number;
  equipmentType: EquipmentTypeEnums;
}) {
  const columnHelper = createColumnHelper<Equipment>();
  const { userModel } = useAppSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment>();
  const handleOpenEmailModal = (equipment: Equipment) => {
    setSelectedEquipment(equipment);
    setShowEmailModal(true);
  };
  const handleAddItemClick = async (equipment: Equipment) => {
    if (userModel) {
      let openOrderId =
        localStorage.getItem("openOrderId") &&
        Number(localStorage.getItem("openOrderId")) >= 0
          ? Number(localStorage.getItem("openOrderId"))
          : 0;
      if (openOrderId === 0) {
        await OrderApi.checkUserOpenOrder(userModel.id)
          .then((res) => {
            if (res.data) {
              openOrderId = res.data.id;
              localStorage.setItem("openOrderId", openOrderId.toString());
            } else {
              const newOrder: Order = {
                id: 0,
                businessName: null,
                contactName: null,
                contactPhone: null,
                contactEmail: null,
                notes: null,
                dateCreated: null,
                orderStatusId: 1,
                userId: userModel.id,
                billingAddressId: null,
                shippingAddressId: null,
                dateSubmitted: null,
                ponumber: null,
                clientId: userModel.clientId,
                shippingMethodId: 15,

                client: null,
                orderItems: [],
                billingAddress: null,
                shippingAddress: null,
                user: null,
              };
              OrderApi.addUpdateOrder(newOrder)
                .then((res) => {
                  if (res.data.success) {
                    openOrderId = Number(res.data.affectedEntityIdentifier);
                    localStorage.setItem("openOrderId", openOrderId.toString());
                  }
                })
                .catch((err) => {
                  setIsLoading(false);
                });
            }
          })
          .catch((err) => {
            setIsLoading(false);
          });
      }
      const newOrderItem: OrderItem = {
        id: 0,
        orderId: openOrderId,
        equipmentId: equipment.id,
        quantity: 1,
        equipment: null,
      };
      await OrderApi.addUpdateOrderItem(newOrderItem)
        .then((res) => {
          if (res.data.success) {
            toast.success("Item added to cart");
          }
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
        });
      await OrderApi.getOrderItemsCount(openOrderId)
        .then((res) => {
          if (res.data !== null) {
            localStorage.setItem("orderItemsCount", res.data.toString());
            window.dispatchEvent(new Event("updateOrderItemCount"));
          }
        })
        .catch((err) => {
          setIsLoading(false);
        });
    }
  };

  const columns = [
    columnHelper.accessor((row) => row.id, {
      id: "id",
      header: "",
      cell: (info) => (
        <div className="flex justify-center gap-1">
          <Link
            to={`/${
              EquipmentTypeStringEnums[equipmentType]
            }/${info.getValue()}`}
            className="buttonMain p-2"
          >
            <Tooltip
              content={`View ${EquipmentTypeStringEnums[equipmentType]}`}
            >
              <HiEye />
            </Tooltip>
          </Link>
        </div>
      ),
      footer: (info) => info.column.id,
      size: 35,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.manufacturer?.name, {
      id: "manufacturerId",
      header: "Manufacturer",
      // cell: (info) => <i>{info.getValue()}</i>,
      sortingFn: "alphanumeric",
      footer: (info) => info.column.id,
      filterFn: "includesString",
    }),
    columnHelper.accessor((row) => row.manufacturer?.rimssOemvendorNumber, {
      id: "rimssOemvendorNumber",
      header: "Vendor Code",
      // cell: (info) => <i>{info.getValue()}</i>,
      sortingFn: "alphanumeric",
      footer: (info) => info.column.id,
      filterFn: "includesString",
    }),
    columnHelper.accessor((row) => row.number, {
      id: "number",
      header: "Number",
      // cell: (info) => <i>{info.getValue()}</i>,
      sortingFn: "alphanumeric",
      footer: (info) => info.column.id,
      filterFn: "includesString",
    }),
    columnHelper.accessor((row) => row.primaryImageURL, {
      header: "Image",
      cell: (info) => (
        <img src={info.getValue()} alt="Profile" style={{ width: "175px" }} />
      ),
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.description, {
      id: "description",
      header: "Description",
      // cell: (info) => <i>{info.getValue()}</i>,
      sortingFn: "alphanumeric",
      filterFn: "includesString",
    }),
    // columnHelper.accessor((row) => row.categoriesString, {
    //   id: 'categoriesString',
    //   header: 'Category',
    //   // cell: (info) => <i>{info.getValue()}</i>,
    //   sortingFn: 'alphanumeric',
    //   filterFn: 'includesString',
    // }),
    columnHelper.accessor((row) => row.cost, {
      id: "cost",
      header: "Cost",
      cell: (info) => <i>${formatNumbers(info.row.original.cost ?? 0)}</i>,
      sortingFn: "alphanumeric",
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.price, {
      id: "price",
      header: "List",
      cell: (info) => <i>${formatNumbers(info.row.original.price ?? 0)}</i>,
      sortingFn: "alphanumeric",
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.tradePrice, {
      id: "tradePrice",
      header: "Trade",
      cell: (info) => (
        <i>${formatNumbers(info.row.original.tradePrice ?? 0)}</i>
      ),
      sortingFn: "alphanumeric",
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.special1Price, {
      id: "special1Price",
      header: "FTL",
      cell: (info) => (
        <i>${formatNumbers(info.row.original.special1Price ?? 0)}</i>
      ),
      sortingFn: "alphanumeric",
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.special2Price, {
      id: "special2Price",
      header: "NET",
      cell: (info) => (
        <i>${formatNumbers(info.row.original.special2Price ?? 0)}</i>
      ),
      sortingFn: "alphanumeric",
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.id, {
      id: "additem",
      header: "Add To Cart",
      cell: (info) => (
        <div className="flex justify-center gap-1">
          {userModel && (
            <div className="flex justify-center">
              <Button
                type="button"
                onClick={() => handleAddItemClick(info.row.original)}
                className="buttonMain"
              >
                Add to Cart
              </Button>
            </div>
          )}
          {userModel && (
            <div className="flex justify-center">
              <Button
                type="button"
                onClick={() => handleOpenEmailModal(info.row.original)}
                className="buttonMain"
              >
                Check Availability
              </Button>
            </div>
          )}
          {!userModel && (
            <div className="flex justify-center">
              <Link to={"/login"}>
                <Button
                  type="button"
                  // onClick={() => handleAddItemClick(equipmentData)}
                  className="buttonMain"
                >
                  Login to order
                </Button>
              </Link>
            </div>
          )}
        </div>
      ),
      footer: (info) => info.column.id,
      size: 35,
      enableColumnFilter: false,
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
      sorting: [
        {
          id: "number",
          desc: false,
        },
      ],
      columnVisibility: {
        manufacturerId: manufacturerId === 0,
        cost: userModel !== null,
        special2Price: userModel !== null,
        tradePrice: userModel !== null,
        special1Price: userModel !== null,
        rimssOemvendorNumber: userModel !== null,
        id: userModel !== null,
        additem: userModel === null,
      },
    },
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  return (
    <>
      <div className="w-full mx-auto overflow-x-auto">
        <PaginatedTable
          table={table}
          dataCount={data.length}
          columnResizeMode="onChange"
          showFilters={true}
        />
        <EmailModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          equipment={selectedEquipment!}
          onSendSuccess={() => alert("Email sent!")}
        />
      </div>
    </>
  );
}
